import React from 'react';
import clsx from 'clsx';
import useEffectiveColorMode from '../../hooks/useEffectiveColorMode';
import CheckoutPageLockup from './CheckoutPageLockup';

export type PoweredByCheckoutPageSurface = 'checkout' | 'confirmation' | 'portal' | 'email' | 'linkInBio';
export type PoweredByCheckoutPageVariant = 'pill' | 'cta';
export type PoweredByCheckoutPagePageType = 'checkout' | 'event' | 'form';

interface Props {
  variant: PoweredByCheckoutPageVariant;
  surface: PoweredByCheckoutPageSurface;
  /** The kind of page the buyer just used. Drives the CTA copy + UTM. */
  pageType?: PoweredByCheckoutPagePageType;
  /** Pill only: horizontal placement within its container. */
  align?: 'center' | 'start';
  /** Private partner perk: Rewardful token. When set, the badge links through
   *  the partner's affiliate URL and the UTM campaign becomes partner_badge. */
  affiliateToken?: string | null;
}

/**
 * Per-page-type CTA copy. The flat attribution line frames it as a credit
 * ("Made with Checkout Page, {tagline}"), with the action on a quiet button
 * below. Deliberately attribution-led so it reads as a footer credit, not a
 * step the buyer must take on the seller's confirmation.
 */
const CTA_COPY: Record<PoweredByCheckoutPagePageType, { tagline: string; action: string }> = {
  checkout: {
    tagline: 'the simplest way to sell products online for free',
    action: 'Create your own checkout page',
  },
  event: {
    tagline: 'the simplest way to sell event tickets for free',
    action: 'Create your own event',
  },
  form: { tagline: 'the simplest way to create forms for free', action: 'Create your own form' },
};

function buildLink(
  surface: PoweredByCheckoutPageSurface,
  pageType: PoweredByCheckoutPagePageType,
  affiliateToken?: string | null,
): string {
  const params = new URLSearchParams({
    ...(affiliateToken ? { via: affiliateToken } : {}),
    utm_source: 'badge',
    utm_medium: surface === 'linkInBio' ? 'link_in_bio' : surface,
    utm_campaign: affiliateToken ? 'partner_badge' : 'free_plan',
    utm_content: pageType,
  });
  return `https://checkoutpage.com/?${params.toString()}`;
}

export default function PoweredByCheckoutPage({
  variant,
  surface,
  pageType = 'checkout',
  align = 'center',
  affiliateToken = null,
}: Props) {
  const href = buildLink(surface, pageType, affiliateToken);
  const isDark = useEffectiveColorMode() === 'dark';

  if (variant === 'pill') {
    // Prefer the background-aware color (set on the checkout surface from the
    // actual page background's luminance); fall back to the mode-based color on
    // surfaces without checkout CSS vars, e.g. the customer portal.
    const badgeColor = `var(--cp_branding-badge-color, ${isDark ? '#ffffff' : '#000000'})`;
    return (
      <div
        className={clsx(
          'cp_powered-by-checkoutpage-pill py-2 w-full flex',
          align === 'start' ? 'justify-start' : 'justify-center',
        )}
      >
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Powered by Checkout Page"
          className="inline-flex items-baseline gap-1.5 rounded-md border px-1.5 py-1.5 text-sm font-medium leading-none"
          style={{ color: badgeColor, borderColor: badgeColor }}
        >
          <span
            className="text-xs font-medium tracking-tight"
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, sans-serif',
            }}
          >
            Powered by
          </span>
          <CheckoutPageLockup className="text-xs font-semibold tracking-tight" />
        </a>
      </div>
    );
  }

  // variant === 'cta'
  // A footer credit set apart from the seller's confirmation by a hairline: a
  // flat attribution line above a quiet outlined button carrying the brand mark.
  // The divider + attribution-led copy keep it from reading as a buyer action.
  return (
    <div
      className={clsx(
        'cp_powered-by-checkoutpage-cta flex w-full flex-col items-center gap-4 border-t py-8 text-center',
        isDark ? 'border-white/10' : 'border-gray-100',
      )}
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, sans-serif',
      }}
    >
      <p
        className={clsx(
          'text-balance text-[13px] leading-snug',
          isDark ? 'text-white/50' : 'text-gray-500',
        )}
      >
        Made with Checkout Page, {CTA_COPY[pageType].tagline}.
      </p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        // Mirrors the dashboard shadcn `outline` button: subtle border, white
        // surface, hover fill, and a press nudge. Hardcoded equivalents because
        // the buyer surface lacks the dashboard's design-token CSS vars.
        className={clsx(
          'inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border px-2.5 text-sm font-medium',
          'transition-colors active:translate-y-px',
          isDark
            ? 'border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.08]'
            : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50',
        )}
      >
        {/* Brand card mark only (no wordmark); the label carries the words. */}
        <svg
          viewBox="0 0 155 84"
          className="h-[0.9em] w-auto shrink-0"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M19 0 L155 0 L136 84 L0 84 Z M30 13 L50 13 L47 26.5 L27 26.5 Z" />
        </svg>
        {CTA_COPY[pageType].action}
      </a>
    </div>
  );
}
